import React, { Fragment } from 'react'
import { CriteriaEvaluationResult, CriteriaSpec, dollars, EquifaxPrequalReport, yyyymmdd } from "@oneethos/shared"
import { FaCircleCheck, FaCircleXmark } from "react-icons/fa6"

type EvaluationProps = {
  evaluation: CriteriaEvaluationResult
  report: EquifaxPrequalReport
  sharedMonthlyDebt?: number
}

const CriteriaCell = ({ spec }: { spec: CriteriaSpec }) => {
  return (
    <td>
      {Object.entries(spec).map(([key, value]) =>
        <div key={key}>
          {key}:&nbsp;
          <b>{value instanceof Date ? yyyymmdd(value as Date) : value.toString()}</b>
        </div>)
      }
    </td>
  )
}

export const EvaluationSummary = ({ evaluation, report, sharedMonthlyDebt }: EvaluationProps) => {
  return <div className={`eval-summary ${evaluation.result}`}>
    <div className="es-label">{evaluation.result === 'pass' ? 'Approve' : 'Decline'}</div>
    <div className="es-detail">${evaluation.estimatedPayment.toFixed(0)}/mo {evaluation.postDti?.toFixed(1)}%</div>
  </div>
}

export const Evaluation = ({ evaluation, report, sharedMonthlyDebt }: EvaluationProps) => {
  if (!evaluation) {
    return <div className="alert alert-danger">
      No evaluation available
    </div>
  }

  return (
    <div className="eval">
      <table className="table mb-3 cr-highlights">
        <tbody>
          <tr>
            <td>Stated Monthly Income</td>
            <td>{dollars(evaluation?.statedGrossMonthlyIncome || 0)}</td>
          </tr>
          <tr>
            <td>Monthly Debt</td>
            <td>{dollars(report.monthlyDebt || 0, 0)}</td>
          </tr>
          {sharedMonthlyDebt ? <tr>
            <td>Shared Monthly Debt</td>
            <td>{dollars(sharedMonthlyDebt || 0, 0)}
            </td>
          </tr> : null}
          <tr>
            <td>Combined Monthly Debt</td>
            <td>{dollars(report.combinedMonthlyDebt || 0, 0)}</td>
          </tr>
          <tr>
            <td>Current DTI</td>
            <td>{evaluation.currentDti?.toFixed(1)}%</td>
          </tr>
          <tr>
            <td>Post DTI</td>
            <td>{evaluation.postDti?.toFixed(1)}%</td>
          </tr>
          <tr>
            <td>Estimated Payment</td>
            <td>{dollars(evaluation.estimatedPayment)}</td>
          </tr>
          <tr>
            <td>Max Allowable Monthly Payment</td>
            <td>{dollars(evaluation.maxAllowableMonthlyPayment)}</td>
          </tr>
        </tbody>
      </table>

      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>Result</th>
              <th>Min</th>
              <th>DTI</th>
              <th>Bankruptcy</th>
              <th>Collections</th>
              <th>Past Due</th>
              <th>Max Payment</th>
            </tr>
          </thead>
          <tbody>
            {evaluation.byCriteria.map((res, i) => <Fragment key={i}>
              <tr>
                <td>{res.result === 'pass' ? <FaCircleCheck color='green' /> : <FaCircleXmark color='red' />}</td>
                <td>{res.criteria.minScore}</td>
                <td>{res.criteria.debtToIncome}</td>
                <CriteriaCell spec={res.criteria.bankruptcy} />
                <CriteriaCell spec={res.criteria.collections} />
                <CriteriaCell spec={res.criteria.pastDue} />
                <td>{res.maxAllowableMonthlyPayment ? dollars(res.maxAllowableMonthlyPayment) : ''}</td>
              </tr>
              {res.result === 'fail' ? <tr>
                <td colSpan={7} >
                  <div className='mb-2'>Reasons:</div>
                  {res.reasons.map((reason, j) => <div key={j}>{reason}</div>)}
                </td>
              </tr> : null}
            </Fragment>)}
          </tbody>
        </table>
      </div>
    </div>)
}

export default Evaluation